import { useAuthContext } from '@/context/auth/useAuthContext';
import { HStack, Link as ChakraLink, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { superAdminMenuOptions } from '../options';
import type { TMenuOption } from '../options';
import { Link, useRouter } from '@tanstack/react-router';
import menuMessages from '@/messages/menu';
import { background } from '@/theme/colors/palettes';
import VerifyEmailConfirmationModal from '@/components/molecules/VerifyEmailConfirmationModal';
import roles from '@/constants/roles';
import sharedRoutes from '@/constants/routes/shared';
import { contentCreatorRoutes, employeeUserRoutes } from '@/constants/routes/app';
import { useAppStore } from '@/store/useAppStore';
import { hasUnacceptedNonContentPolicyDocument } from '@/utils/onboardingUtils';
import {
  dashboardHomes,
  dashboardLabels,
  getAvailableDashboards,
} from '@/components/molecules/Header/dashboardNavigation';

interface Props {
  onPressCallback?: () => void;
}

const ProfileMenu = ({ onPressCallback = () => {} }: Props) => {
  const { isAdmin, isPeerSupport, isContentCreator, isSuperAdmin, signOut, user } = useAuthContext();
  const { legalAcceptanceStatuses, setActiveDashboard } = useAppStore();
  const {
    state: {
      location: { pathname },
    },
    navigate,
  } = useRouter();
  const [isVerifyEmailModalOpen, setIsVerifyEmailModalOpen] = useState(false);
  const isOnboarding = pathname.includes('/onboarding');
  const hasPendingEulaUpdate = hasUnacceptedNonContentPolicyDocument(legalAcceptanceStatuses);
  const canBecomeCreator =
    !isContentCreator && (isAdmin || isPeerSupport || !!user?.roles.includes(roles.peerSupportLead));
  const dashboards = getAvailableDashboards(user?.roles);
  const isContentCreatorOnly = isContentCreator && user?.roles.length === 1;

  const secondaryOptions: TMenuOption[] =
    isOnboarding || hasPendingEulaUpdate || isSuperAdmin
      ? []
      : [
          {
            label: menuMessages.myProfile,
            navigateTo: isContentCreatorOnly ? contentCreatorRoutes.creatorProfile : employeeUserRoutes.userProfile,
          },
          { label: menuMessages.settings, navigateTo: sharedRoutes.settings },
          ...(canBecomeCreator ? [{ label: menuMessages.becomeCreator, navigateTo: sharedRoutes.creatorApply }] : []),
          { label: menuMessages.contactSupport, navigateTo: sharedRoutes.contactSupport },
        ];

  const handleSignOut = () => {
    signOut();
    navigate({ to: '/sign-in' });
  };

  const renderMenuOption = (option: TMenuOption, index: number, onSelect?: () => void) => (
    <ChakraLink
      as={Link}
      key={`${index}-${option.label}`}
      to={option.navigateTo}
      textAlign="right"
      fontWeight="600"
      fontSize={'14px'}
      color={pathname === option.navigateTo ? 'primary.500' : 'text.mediumBlue'}
      _hover={{ opacity: 0.7 }}
      onClick={(event) => {
        if (option.navigateTo === sharedRoutes.creatorApply && !user?.email_verified) {
          event.preventDefault();
          event.stopPropagation();
          setIsVerifyEmailModalOpen(true);
        } else {
          onSelect?.();
        }
        onPressCallback();
      }}
    >
      {option.label}
    </ChakraLink>
  );

  return (
    <>
      <VStack padding="20px" alignItems="flex-end" gap={2}>
        {!isSuperAdmin && !isOnboarding && !hasPendingEulaUpdate && dashboards.length >= 2 && (
          <>
            <Text color="text.mediumGray" fontWeight="800" fontSize={'10px'} lineHeight={'12px'}>
              YOUR DASHBOARDS
            </Text>
            {dashboards.map((dashboard, index) =>
              renderMenuOption(
                { label: dashboardLabels[dashboard], navigateTo: dashboardHomes[dashboard] },
                index,
                () => setActiveDashboard(dashboard),
              ),
            )}
            <HStack width="100%" height="1px" backgroundColor={background.separatorBlue} />
          </>
        )}
        {isSuperAdmin &&
          !isOnboarding &&
          !hasPendingEulaUpdate &&
          superAdminMenuOptions.map((option, index) => renderMenuOption(option, index))}
        {secondaryOptions.map((option, index) => renderMenuOption(option, index))}
        <HStack onClick={handleSignOut} onClickCapture={onPressCallback} _hover={{ cursor: 'pointer' }}>
          <Text color="text.mediumGray" fontWeight="600" fontSize={'14px'}>
            {menuMessages.signOut}
          </Text>
        </HStack>
      </VStack>
      <VerifyEmailConfirmationModal
        isOpen={isVerifyEmailModalOpen}
        onClose={() => setIsVerifyEmailModalOpen(false)}
        email={user?.email}
      />
    </>
  );
};

export default ProfileMenu;
